import { useState } from "react";
import { Icon } from "../../components/Icon";
import { Avatar, Badge, Btn, Card, PageHead, Skeleton, StatTile } from "../../components/kit";
import { useMe } from "../../lib/api";
import {
  useOrg, inviteMember,
  useOrgTracks, useOrgAssignments, useOrgMemberOptions, assignOrgItem, unassignOrgTrack,
  type OrgAssignment,
} from "../../lib/hooks";

export function TeamPage() {
  const me = useMe();
  const org = useOrg(me?.email);
  const isAdmin = me?.role === "admin";

  const handleInvite = () => {
    const email = window.prompt("Invite a teammate — their email address")?.trim();
    if (!email) return;
    inviteMember(email)
      .then(() => window.alert(`Invitation sent to ${email}.`))
      .catch(() => window.alert("Couldn’t send that invite — check the email, or that you have admin rights."));
  };

  const loading = !org;
  const members = org?.members ?? [];
  const seats = me?.raw?.org?.seats;
  const seatsLabel = loading ? "—" : seats ? `${members.length} / ${seats}` : String(members.length);
  const avgProgress = loading ? "—" : `${org.avgProgress}%`;
  const { assignments, reload: reloadAssignments } = useOrgAssignments();
  const sharedCount = assignments ? String(assignments.length) : "—";

  return (
    <div className="page">
      <PageHead
        title="Team / Org"
        sub="Seats, shared learning, and org-level controls."
        actions={<Btn variant="live" icon="user-plus" onClick={handleInvite}>Invite member</Btn>}
      />

      <div className="stat-row">
        <StatTile icon="users" value={loading ? "—" : String(members.length)} label="Members" />
        <StatTile icon="armchair" value={seatsLabel} label="Seats used" tone="live" />
        <StatTile icon="route" value={sharedCount} label="Shared items" />
        <StatTile icon="trending-up" value={avgProgress} label="Avg. progress" />
      </div>

      <Card title="Members" icon="users" count={loading ? undefined : members.length} pad={false}>
        <div className="member-list">
          {loading && Array.from({ length: 4 }).map((_, i) => (
            <div className="member" key={`sk-${i}`} style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Skeleton w={38} h={38} r={19} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}><Skeleton w={140} h={14} /><Skeleton w={180} h={11} /></div>
            </div>
          ))}
          {members.map((m, i) => (
            <div className="member" key={i}>
              <Avatar initials={m.initials} size={38} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="member-n">
                  {m.name}
                  {m.you && <span className="you-tag">You</span>}
                </div>
                <div className="muted-sm">{m.email}</div>
              </div>
              <span className={`role-pill ${m.role.toLowerCase()}`}>{m.role}</span>
              <button className="icon-mini"><Icon name="more-horizontal" size={16} /></button>
            </div>
          ))}
        </div>
      </Card>

      <div className="two-col">
        <SharedTracksCard isAdmin={isAdmin} assignments={assignments} reload={reloadAssignments} />

        <Card title="Admin & security" icon="shield-check" pad={false}>
          <div className="set-list">
            <div className="set-row"><span className="set-ic"><Icon name="key-round" size={17} /></span><div className="set-body"><div className="set-t">Single sign-on (SSO)</div><div className="set-d">SAML / Okta</div></div><Badge tone="amber">Not set up</Badge></div>
            <div className="set-row"><span className="set-ic"><Icon name="building-2" size={17} /></span><div className="set-body"><div className="set-t">Org domain</div><div className="set-d">{me?.email?.split("@")[1] ?? "adaptr.studio"}</div></div><Badge tone="live" icon="check">Verified</Badge></div>
            <div className="set-row"><span className="set-ic"><Icon name="file-lock-2" size={17} /></span><div className="set-body"><div className="set-t">Data residency & retention</div><div className="set-d">Org-wide policy</div></div><Btn variant="ghost" size="sm">Configure</Btn></div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* Live shared assignments + (admin-only) an inline assign panel that can push a
   track OR a single lesson to the whole team OR specific members. */
function SharedTracksCard({ isAdmin, assignments, reload }: { isAdmin: boolean; assignments: OrgAssignment[] | null; reload: () => void }) {
  const catalog = useOrgTracks();
  const members = useOrgMemberOptions();
  const [assigning, setAssigning] = useState(false);
  const [itemType, setItemType] = useState<"track" | "lesson">("track");
  const [itemId, setItemId] = useState("");
  const [scope, setScope] = useState<"org" | "user">("org");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reset = () => { setAssigning(false); setItemId(""); setMemberIds([]); setScope("org"); setItemType("track"); };
  const toggleMember = (id: string) =>
    setMemberIds((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  const doAssign = () => {
    if (!itemId || busy) return;
    if (scope === "user" && memberIds.length === 0) return;
    setBusy(true);
    assignOrgItem(itemType, itemId, scope, scope === "user" ? memberIds : undefined)
      .then(() => { reset(); reload(); })
      .catch(() => window.alert(`Couldn’t assign that ${itemType} — admin rights are required.`))
      .finally(() => setBusy(false));
  };
  const remove = (id: string) => {
    unassignOrgTrack(id).then(reload).catch(() => window.alert("Couldn’t remove that assignment."));
  };

  const live = assignments ?? [];
  const tracks = catalog?.tracks ?? [];
  const lessons = catalog?.lessons ?? [];

  return (
    <Card title="Shared learning" icon="route" iconColor="var(--live-dim)"
      action={isAdmin ? <Btn variant="ghost" size="sm" icon={assigning ? "x" : "plus"} onClick={() => (assigning ? reset() : setAssigning(true))}>{assigning ? "Cancel" : "Assign"}</Btn> : undefined}>
      {assigning && isAdmin && (
        <div className="assign-panel">
          <div className="seg full">
            {(["track", "lesson"] as const).map((it) => (
              <button key={it} aria-pressed={itemType === it} onClick={() => { setItemType(it); setItemId(""); }}>{it === "track" ? "A track" : "A lesson"}</button>
            ))}
          </div>
          <select className="assign-select" style={{ marginTop: 8 }} value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">{itemType === "track" ? "Choose a track…" : "Choose a lesson…"}</option>
            {itemType === "track"
              ? tracks.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.lessonCount} lessons</option>)
              : lessons.map((l) => <option key={l.id} value={l.id}>{l.title}{l.durationMinutes ? ` · ${l.durationMinutes} min` : ""}</option>)}
          </select>
          <div className="seg" style={{ marginTop: 8 }}>
            {(["org", "user"] as const).map((s) => (
              <button key={s} aria-pressed={scope === s} onClick={() => setScope(s)}>{s === "org" ? "Whole team" : "Specific members"}</button>
            ))}
          </div>
          {scope === "user" && (
            <div className="member-picker">
              {(members ?? []).map((m) => (
                <button key={m.id} type="button" className={`member-chip ${memberIds.includes(m.id) ? "on" : ""}`} onClick={() => toggleMember(m.id)}>
                  <Icon name={memberIds.includes(m.id) ? "check" : "plus"} size={12} strokeWidth={2.4} />{m.email}
                </button>
              ))}
              {(members ?? []).length === 0 && <span className="muted-sm">No other members to assign to yet.</span>}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <Btn variant="live" size="sm" icon="check" onClick={doAssign}>
              {busy ? "Assigning…" : scope === "user" && memberIds.length > 1 ? `Assign to ${memberIds.length}` : `Assign ${itemType}`}
            </Btn>
          </div>
        </div>
      )}
      <div className="shared-list">
        {live.map((a) => (
          <div className="shared" key={a.id}>
            <span className="shared-ic"><Icon name={a.icon || "route"} size={15} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mcp-t">{a.name}{a.itemType === "lesson" && <span className="you-tag">Lesson</span>}</div>
              <div className="muted-sm">{a.itemType === "lesson" ? "Single lesson" : `${a.lessonCount} lessons`} · {a.assignedTo}</div>
            </div>
            {isAdmin && <button className="icon-mini" title="Remove assignment" onClick={() => remove(a.id)}><Icon name="x" size={14} /></button>}
          </div>
        ))}
        {assignments !== null && live.length === 0 && (
          <div className="muted-sm" style={{ padding: "6px 2px" }}>
            No shared learning yet.{isAdmin ? " Use “Assign” to give your team a track or a single lesson." : " An admin can assign tracks and lessons here."}
          </div>
        )}
        {assignments === null && Array.from({ length: 2 }).map((_, i) => (
          <div className="shared" key={`sk-${i}`} style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Skeleton w={32} h={32} r={9} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}><Skeleton w="50%" h={13} /><Skeleton w="35%" h={11} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}
